import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Loader2, Save, Trash2, Plus, Filter, ArrowLeftRight, Table2, X, ChevronDown, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { salesAPI, suppliersAPI, industrialProductsAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const DIMENSIONS = [
    { id: 'produit', label: 'Produit' },
    { id: 'code_meti', label: 'Code Méti' },
    { id: 'categorie', label: 'Catégorie' },
    { id: 'famille', label: 'Famille' },
    { id: 'fournisseur', label: 'Fournisseur' },
    { id: 'site', label: 'Site' },
    { id: 'jour', label: 'Jour' },
    { id: 'semaine', label: 'Semaine' },
    { id: 'mois', label: 'Mois' },
];

const METRICS = [
    { id: 'qty', label: 'Qté', fmt: (v) => (v || 0).toFixed(0) },
    { id: 'ca', label: 'CA', fmt: (v) => formatCurrency(v || 0) },
    { id: 'prix_moyen', label: 'Prix moy.', fmt: (v) => formatCurrency(v || 0) },
    { id: 'qty_loss', label: 'Qté pertes', fmt: (v) => (v || 0).toFixed(0) },
    { id: 'value_loss', label: 'Val. pertes', fmt: (v) => formatCurrency(v || 0) },
    { id: 'couverture', label: '% cov.', fmt: (v) => `${(v || 0).toFixed(1)}%` },
];

const FE_SITES = ['FE Génipa', 'FE Cluny', 'FE Dillon', 'FE Océanis', 'FE Vauclin'];

export default function PivotTab({ sites, active = true }) {
    const [rows, setRows] = useState(['produit']);
    const [cols, setCols] = useState(['site']);
    const [values, setValues] = useState(['qty', 'ca']);
    const [filterSiteIds, setFilterSiteIds] = useState([]);
    const [filterCategories, setFilterCategories] = useState([]);
    const [filterSupplierIds, setFilterSupplierIds] = useState([]);
    const [startDate, setStartDate] = useState(daysAgoStr(30));
    const [endDate, setEndDate] = useState(todayStr());

    const [pivot, setPivot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [presets, setPresets] = useState([]);
    const [selectedPreset, setSelectedPreset] = useState('');
    const [presetName, setPresetName] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showDateInputs, setShowDateInputs] = useState(false);

    const feSites = useMemo(() => sites.filter(s => FE_SITES.includes(s.name)), [sites]);
    const primaryMetric = values[0] || 'qty';

    useEffect(() => {
        (async () => {
            try {
                const [c, s, p] = await Promise.all([
                    industrialProductsAPI.getCategories(),
                    suppliersAPI.getAll(),
                    salesAPI.listPresets(),
                ]);
                setCategories(c.data || []);
                setSuppliers(s.data || []);
                const presetList = p.data || [];
                setPresets(presetList);

                // Default range = current month (1st → last day)
                const now = new Date();
                const y = now.getFullYear();
                const m = now.getMonth() + 1;
                const lastDay = new Date(y, m, 0).getDate();
                const firstIso = `${y}-${String(m).padStart(2, '0')}-01`;
                const lastIso = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

                // Auto-load the "TCD Qté Vendues" preset if it exists (silent)
                const defaultPreset = presetList.find(p => p.name === 'TCD Qté Vendues');
                if (defaultPreset) {
                    const cfg = defaultPreset.config || {};
                    if (cfg.rows) setRows(cfg.rows);
                    if (cfg.cols) setCols(cfg.cols);
                    if (cfg.values) setValues(METRICS.filter(m => cfg.values.includes(m.id)).map(m => m.id));
                    const f = cfg.filters || {};
                    setFilterSiteIds(f.site_ids || []);
                    setFilterCategories(f.categories || []);
                    setFilterSupplierIds(f.supplier_ids || []);
                    // Always force current month, ignoring any saved date range
                    setStartDate(firstIso);
                    setEndDate(lastIso);
                    setSelectedPreset(defaultPreset.id);
                } else {
                    setStartDate(firstIso);
                    setEndDate(lastIso);
                }
            } catch (e) { console.error(e); }
        })();
    }, []);

    const reloadPresets = async () => {
        const r = await salesAPI.listPresets();
        setPresets(r.data || []);
    };

    const runPivot = async () => {
        if (!rows.length && !cols.length) {
            toast.error('Sélectionnez au moins une ligne ou une colonne');
            return;
        }
        setLoading(true);
        try {
            const r = await salesAPI.pivot({
                rows, cols, values,
                filters: {
                    start_date: startDate,
                    end_date: endDate,
                    site_ids: filterSiteIds.length ? filterSiteIds : undefined,
                    categories: filterCategories.length ? filterCategories : undefined,
                    supplier_ids: filterSupplierIds.length ? filterSupplierIds : undefined,
                },
            });
            setPivot(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de calcul');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(() => { runPivot(); }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [rows.join('|'), cols.join('|'), values.join('|'), startDate, endDate, filterSiteIds.join(','), filterCategories.join(','), filterSupplierIds.join(',')]);

    const loadPreset = (id) => {
        const p = presets.find(p => p.id === id);
        if (!p) return;
        const cfg = p.config || {};
        setRows(cfg.rows || []);
        setCols(cfg.cols || []);
        setValues(METRICS.filter(m => (cfg.values || ['qty']).includes(m.id)).map(m => m.id));
        const f = cfg.filters || {};
        setFilterSiteIds(f.site_ids || []);
        setFilterCategories(f.categories || []);
        setFilterSupplierIds(f.supplier_ids || []);
        if (f.start_date) setStartDate(f.start_date);
        if (f.end_date) setEndDate(f.end_date);
        setSelectedPreset(id);
        toast.success(`« ${p.name} » chargé`);
    };

    const savePreset = async () => {
        const name = (presetName || '').trim();
        if (!name) { toast.error('Donnez un nom au preset'); return; }
        const config = {
            rows, cols, values,
            filters: {
                start_date: startDate, end_date: endDate,
                site_ids: filterSiteIds, categories: filterCategories, supplier_ids: filterSupplierIds,
            },
        };
        try {
            const existing = presets.find(p => p.name.toLowerCase() === name.toLowerCase());
            if (existing) {
                await salesAPI.updatePreset(existing.id, { name, config });
                toast.success('Preset mis à jour');
                setSelectedPreset(existing.id);
            } else {
                const r = await salesAPI.createPreset({ name, config });
                toast.success('Preset enregistré');
                setSelectedPreset(r.data.id);
            }
            setPresetName('');
            reloadPresets();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const deletePreset = async () => {
        if (!selectedPreset) return;
        const p = presets.find(p => p.id === selectedPreset);
        if (!window.confirm(`Supprimer le preset « ${p?.name} » ?`)) return;
        await salesAPI.deletePreset(selectedPreset);
        toast.success('Preset supprimé');
        setSelectedPreset('');
        reloadPresets();
    };

    const maxCellValue = useMemo(() => {
        if (!pivot) return 0;
        let max = 0;
        for (const k of Object.keys(pivot.cells)) {
            const v = pivot.cells[k][primaryMetric] || 0;
            if (v > max) max = v;
        }
        return max;
    }, [pivot, primaryMetric]);

    const cellBg = (val) => {
        if (!val || !maxCellValue) return '';
        const ratio = val / maxCellValue;
        if (ratio < 0.05) return 'bg-slate-50';
        if (ratio < 0.2) return 'bg-emerald-50';
        if (ratio < 0.4) return 'bg-emerald-100';
        if (ratio < 0.6) return 'bg-emerald-200';
        if (ratio < 0.8) return 'bg-emerald-300';
        return 'bg-emerald-400 text-emerald-900';
    };

    const fmtMetric = (mid, v) => {
        const m = METRICS.find(x => x.id === mid);
        return m ? m.fmt(v) : v;
    };

    const swap = () => { setRows(cols); setCols(rows); };

    // Site shortcuts (similar to Ventes tab)
    const toggleSiteShortcut = (siteId) => {
        if (siteId === 'all') { setFilterSiteIds([]); return; }
        if (filterSiteIds.includes(siteId)) {
            setFilterSiteIds(filterSiteIds.filter(x => x !== siteId));
        } else {
            setFilterSiteIds([...filterSiteIds, siteId]);
        }
    };
    const isAllSites = filterSiteIds.length === 0;

    // Week selection from WeekPicker
    const onWeekChange = (start, end) => { setStartDate(start); setEndDate(end); };

    return (
        <div className="space-y-2" data-testid="pivot-tab">
            {/* Toolbar */}
            <Card>
                <CardHeader className="py-2 px-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <Table2 className="w-4 h-4 text-purple-500" />
                        <CardTitle className="text-sm">TCD Ventes</CardTitle>

                        {/* Site shortcuts */}
                        <div className="flex flex-wrap items-center gap-1 ml-2" data-testid="pivot-site-shortcuts">
                            <button
                                type="button"
                                onClick={() => toggleSiteShortcut('all')}
                                className={`px-2.5 h-6 rounded-full text-[11px] font-semibold border ${isAllSites ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
                            >Tous FE</button>
                            {feSites.map(s => {
                                const short = s.name.replace('FE ', '');
                                const active = filterSiteIds.includes(s.id);
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => toggleSiteShortcut(s.id)}
                                        className={`px-2.5 h-6 rounded-full text-[11px] font-semibold border ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
                                        data-testid={`pivot-site-${short.toLowerCase()}`}
                                    >{short}</button>
                                );
                            })}
                        </div>

                        <div className="ml-auto flex flex-wrap items-center gap-1.5">
                            <Select value={selectedPreset || 'none'} onValueChange={(v) => v !== 'none' && loadPreset(v)}>
                                <SelectTrigger className="h-7 w-[150px] text-[11px]" data-testid="preset-select">
                                    <SelectValue placeholder="Preset…" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— Aucun —</SelectItem>
                                    {presets.map(p => (
                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedPreset && (
                                <Button size="sm" variant="ghost" className="h-7 px-1.5 text-red-500" onClick={deletePreset} data-testid="preset-delete">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            )}
                            <Input
                                value={presetName}
                                onChange={(e) => setPresetName(e.target.value)}
                                placeholder="Nom"
                                className="h-7 w-[120px] text-[11px]"
                                data-testid="preset-name-input"
                            />
                            <Button size="sm" className="h-7 px-2 text-[11px]" onClick={savePreset} data-testid="preset-save">
                                <Save className="w-3 h-3 mr-1" />Save
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-2 pt-1 pb-2 px-3">
                    {/* Dimension pickers — single line each */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-1.5">
                        <DimensionPicker title="Lignes" selected={rows} setSelected={setRows} color="blue" testid="rows" />
                        <DimensionPicker title="Colonnes" selected={cols} setSelected={setCols} color="purple" testid="cols" />
                        <ValuePicker selected={values} setSelected={setValues} />
                    </div>

                    {/* Week picker */}
                    <CompactWeekPicker
                        feSites={feSites}
                        filterSiteIds={filterSiteIds}
                        startDate={startDate}
                        endDate={endDate}
                        onChange={onWeekChange}
                        active={active}
                    />

                    {/* Bottom toolbar: filters, swap, stats */}
                    <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setShowFilters(v => !v)} data-testid="toggle-filters">
                            <Filter className="w-3 h-3 mr-1" />
                            Filtres avancés {(filterCategories.length + filterSupplierIds.length) > 0 && (
                                <Badge className="ml-1 h-3.5 px-1 bg-slate-900 text-white text-[9px]">
                                    {filterCategories.length + filterSupplierIds.length}
                                </Badge>
                            )}
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={swap} data-testid="swap-axes">
                            <ArrowLeftRight className="w-3 h-3 mr-1" />Permuter
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-slate-500" onClick={() => setShowDateInputs(v => !v)}>
                            <Calendar className="w-3 h-3 mr-1" />Dates précises
                        </Button>
                        {showDateInputs && (
                            <>
                                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-6 text-[11px] w-[125px]" data-testid="pivot-start" />
                                <span className="text-[11px] text-slate-400">→</span>
                                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-6 text-[11px] w-[125px]" data-testid="pivot-end" />
                            </>
                        )}
                        <div className="ml-auto flex gap-1.5 text-[11px]">
                            {pivot && (
                                <>
                                    <Badge variant="outline" className="text-[10px] h-5">{pivot.rows.length} × {pivot.cols.length}</Badge>
                                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px] h-5">CA : {formatCurrency(pivot.grand_total.ca || 0)}</Badge>
                                    <Badge className="bg-blue-100 text-blue-700 text-[10px] h-5">Qté : {(pivot.grand_total.qty || 0).toFixed(0)}</Badge>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Advanced filters (collapsible) */}
                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 border-t border-slate-100">
                            <MultiPicker
                                title="Catégories"
                                items={categories.map(c => ({ id: c, label: c }))}
                                selected={filterCategories}
                                setSelected={setFilterCategories}
                                testid="filter-categories"
                            />
                            <MultiPicker
                                title="Fournisseurs"
                                items={suppliers.map(s => ({ id: s.id, label: s.name }))}
                                selected={filterSupplierIds}
                                setSelected={setFilterSupplierIds}
                                testid="filter-suppliers"
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pivot table */}
            <Card>
                <CardContent className="p-0 overflow-x-auto max-h-[72vh] overflow-y-auto">
                    {loading ? (
                        <div className="py-8 text-center text-slate-400 text-sm">
                            <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                        </div>
                    ) : !pivot || (!pivot.rows.length && !pivot.cols.length) ? (
                        <div className="py-12 text-center text-slate-400 text-sm">
                            Configurez les lignes/colonnes/valeurs ou élargissez la période.
                        </div>
                    ) : (
                        <PivotTable pivot={pivot} primaryMetric={primaryMetric} cellBg={cellBg} fmtMetric={fmtMetric} />
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ----------- Dimension Picker (compact, popover for adding) -----------

function DimensionPicker({ title, selected, setSelected, color = 'blue', testid }) {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const toggle = (id) => {
        if (selected.includes(id)) setSelected(selected.filter(x => x !== id));
        else setSelected([...selected, id]);
    };
    const move = (from, to) => {
        const arr = [...selected];
        const [el] = arr.splice(from, 1);
        arr.splice(to, 0, el);
        setSelected(arr);
    };
    const colorClass = color === 'blue'
        ? 'bg-blue-50 border-blue-300 text-blue-900'
        : 'bg-purple-50 border-purple-300 text-purple-900';
    const available = DIMENSIONS.filter(d => !selected.includes(d.id));

    return (
        <div data-testid={`dim-picker-${testid}`} className="flex items-center gap-1.5 min-h-[24px]">
            <span className="text-[10px] uppercase font-semibold text-slate-500 w-14 shrink-0">{title}</span>
            <div className="flex flex-wrap gap-1 items-center flex-1">
                {selected.length === 0 && (
                    <span className="text-[10px] text-slate-400 italic">aucun</span>
                )}
                {selected.map((id, idx) => {
                    const dim = DIMENSIONS.find(d => d.id === id);
                    return (
                        <span
                            key={id}
                            className={`inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full border text-[11px] font-semibold ${colorClass}`}
                            data-testid={`${testid}-chip-${id}`}
                        >
                            {idx > 0 && (
                                <button type="button" className="opacity-50 hover:opacity-100 leading-none" onClick={() => move(idx, idx - 1)} title="Avant">‹</button>
                            )}
                            <span className="px-0.5">{dim?.label || id}</span>
                            {idx < selected.length - 1 && (
                                <button type="button" className="opacity-50 hover:opacity-100 leading-none" onClick={() => move(idx, idx + 1)} title="Après">›</button>
                            )}
                            <button type="button" onClick={() => toggle(id)} className="opacity-60 hover:opacity-100"><X className="w-2.5 h-2.5" /></button>
                        </span>
                    );
                })}
                {available.length > 0 && (
                    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-dashed border-slate-300 text-slate-400 hover:text-slate-700 hover:border-slate-500"
                                title={`Ajouter une dimension`}
                                data-testid={`${testid}-add-btn`}
                            >
                                <Plus className="w-3 h-3" />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-44 p-1.5" align="start">
                            <div className="text-[10px] uppercase text-slate-400 mb-1 px-1">Ajouter</div>
                            <div className="flex flex-col gap-0.5">
                                {available.map(d => (
                                    <button
                                        key={d.id}
                                        type="button"
                                        onClick={() => { toggle(d.id); setPopoverOpen(false); }}
                                        className="text-left px-2 py-1 text-[11px] rounded hover:bg-slate-100"
                                        data-testid={`${testid}-add-${d.id}`}
                                    >
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
        </div>
    );
}

function ValuePicker({ selected, setSelected }) {
    // Always keep the canonical METRICS order so that columns are stable no matter
    // in which order the user clicks the chips (Qté always before CA, etc.).
    const sortCanonical = (ids) => METRICS.filter(m => ids.includes(m.id)).map(m => m.id);
    const toggle = (id) => {
        if (selected.includes(id)) {
            if (selected.length > 1) setSelected(sortCanonical(selected.filter(x => x !== id)));
        } else {
            setSelected(sortCanonical([...selected, id]));
        }
    };
    return (
        <div data-testid="value-picker" className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase font-semibold text-slate-500 w-14 shrink-0">Valeurs</span>
            <div className="flex flex-wrap gap-1">
                {METRICS.map(m => {
                    const active = selected.includes(m.id);
                    const isPrimary = active && selected[0] === m.id;
                    return (
                        <button
                            key={m.id}
                            type="button"
                            onClick={() => toggle(m.id)}
                            className={`px-2 h-5 rounded-full border text-[11px] font-semibold transition-colors ${
                                isPrimary
                                    ? 'bg-emerald-500 text-white border-emerald-500 ring-1 ring-emerald-300'
                                    : active
                                        ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                                        : 'bg-white text-slate-500 border-slate-200 hover:border-emerald-300'
                            }`}
                            title={isPrimary ? 'Métrique principale (heatmap)' : active ? 'Métrique secondaire' : 'Activer'}
                            data-testid={`value-${m.id}`}
                        >
                            {m.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function MultiPicker({ title, items, selected, setSelected, testid }) {
    const toggle = (id) => {
        if (selected.includes(id)) setSelected(selected.filter(x => x !== id));
        else setSelected([...selected, id]);
    };
    const clear = () => setSelected([]);
    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-1">
                <Label className="text-[10px] uppercase font-semibold text-slate-500">{title}</Label>
                {selected.length > 0 && (
                    <button type="button" onClick={clear} className="text-[10px] text-slate-400 hover:text-red-500">Effacer</button>
                )}
            </div>
            <div className="border border-slate-200 rounded-md p-1.5 max-h-[120px] overflow-y-auto space-y-0.5 bg-white">
                {items.length === 0 && <div className="text-[10px] text-slate-400 italic">Aucun</div>}
                {items.map(it => (
                    <label key={it.id} className="flex items-center gap-1.5 text-[11px] cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                        <Checkbox
                            checked={selected.includes(it.id)}
                            onCheckedChange={() => toggle(it.id)}
                            data-testid={`${testid}-${it.id}`}
                        />
                        <span className="text-slate-700">{it.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );
}

// ----------- Compact Week Picker (with shift+click range) -----------

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
// Fixed widths so months align with their weeks
const WEEK_W = 44; // px
const WEEK_GAP = 2; // px

function CompactWeekPicker({ feSites, filterSiteIds, startDate, endDate, onChange, active = true }) {
    const [coverage, setCoverage] = useState(null);
    const scrollerRef = useRef(null);
    const [anchor, setAnchor] = useState(null);

    const weeks = useMemo(() => {
        // Range: from 1st Monday of November 2020 (début historique) up to ~4 weeks after today
        const today = new Date();
        let start = new Date(2020, 10, 1);
        const dow = start.getDay();
        const diff = dow === 0 ? -6 : 1 - dow;
        start.setDate(start.getDate() + diff);
        let end = new Date(today);
        const tdow = end.getDay();
        const tdiff = tdow === 0 ? -6 : 1 - tdow;
        end.setDate(end.getDate() + tdiff + 7 * 4);
        const ws = [];
        let cur = new Date(start);
        while (cur <= end) { ws.push(new Date(cur)); cur.setDate(cur.getDate() + 7); }
        return ws;
    }, []);

    // Group consecutive weeks by their "principal" month (ISO: month of Thursday)
    const monthGroups = useMemo(() => {
        const groups = [];
        let current = null;
        for (let i = 0; i < weeks.length; i++) {
            const mon = weeks[i];
            const thu = addDays(mon, 3); // ISO week month rule
            const y = thu.getFullYear();
            const m = thu.getMonth() + 1; // 1..12
            const key = `${y}-${m}`;
            if (!current || current.key !== key) {
                current = { key, year: y, month: m, startIdx: i, nWeeks: 1 };
                groups.push(current);
            } else {
                current.nWeeks += 1;
            }
        }
        return groups;
    }, [weeks]);

    const rangeStartIso = useMemo(() => weeks.length ? toIsoDate(weeks[0]) : null, [weeks]);
    const rangeEndIso = useMemo(() => weeks.length ? toIsoDate(addDays(weeks[weeks.length - 1], 6)) : null, [weeks]);

    useEffect(() => {
        if (!rangeStartIso || !rangeEndIso) return;
        (async () => {
            try {
                const r = await salesAPI.coverage({ start_date: rangeStartIso, end_date: rangeEndIso });
                setCoverage(r.data);
            } catch (e) { /* silent */ }
        })();
    }, [rangeStartIso, rangeEndIso]);

    const coverageMap = useMemo(() => {
        if (!coverage) return null;
        const targetIds = !filterSiteIds || filterSiteIds.length === 0
            ? new Set(feSites.map(s => s.id))
            : new Set(filterSiteIds);
        const m = {};
        for (const [sid, d] of coverage.pairs) {
            if (!targetIds.has(sid)) continue;
            const monIso = mondayOf(d);
            if (!m[monIso]) m[monIso] = new Set();
            m[monIso].add(d);
        }
        return m;
    }, [coverage, filterSiteIds, feSites]);

    const selectedMon = startDate ? mondayOf(startDate) : null;
    const selectedEndMon = endDate ? mondayOf(endDate) : null;
    const isSelected = (monIso) => {
        if (!selectedMon || !selectedEndMon) return false;
        return monIso >= selectedMon && monIso <= selectedEndMon;
    };

    // Detect which month is "currently selected" (covers whole month exactly)
    const selectedMonthKey = useMemo(() => {
        if (!startDate || !endDate) return null;
        if (startDate.slice(8, 10) !== '01') return null;
        const [y, m] = startDate.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        const expectedEnd = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        if (endDate !== expectedEnd) return null;
        return `${y}-${m}`;
    }, [startDate, endDate]);

    const handleMonthClick = (year, monthIdx) => {
        const lastDay = new Date(year, monthIdx, 0).getDate();
        const s = `${year}-${String(monthIdx).padStart(2, '0')}-01`;
        const e = `${year}-${String(monthIdx).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        onChange(s, e);
    };

    // Scroll the horizontal week strip so that the current week is visible.
    // We depend on `active`: the PivotTab is `forceMount`ed (always in the DOM),
    // so when it is initially hidden the scroll would happen while the container
    // has clientWidth=0. Re-running when the tab becomes visible ensures the
    // strip is properly centered on the current week every time the user opens
    // the TCD tab.
    useEffect(() => {
        if (!active) return;
        if (!scrollerRef.current || !weeks.length) return;
        const todayMon = mondayOf(toIsoDate(new Date()));
        const idx = weeks.findIndex(w => toIsoDate(w) === todayMon);
        if (idx < 0) return;
        const raf = requestAnimationFrame(() => {
            const container = scrollerRef.current;
            if (!container) return;
            const centerOffset = (container.clientWidth / 2) - (WEEK_W / 2);
            const target = idx * (WEEK_W + WEEK_GAP) - centerOffset;
            container.scrollLeft = Math.max(0, target);
        });
        return () => cancelAnimationFrame(raf);
    }, [weeks, active]);

    const handleClick = (mon, e) => {
        const monIso = toIsoDate(mon);
        const sun = addDays(mon, 6);
        const sunIso = toIsoDate(sun);
        if (e.shiftKey && anchor) {
            const a = anchor;
            const b = monIso;
            const lo = a < b ? a : b;
            const hi = a < b ? b : a;
            const hiSun = toIsoDate(addDays(new Date(hi + 'T00:00:00'), 6));
            onChange(lo, hiSun);
        } else {
            setAnchor(monIso);
            onChange(monIso, sunIso);
        }
    };

    const goPrev = () => { if (scrollerRef.current) scrollerRef.current.scrollLeft -= 200; };
    const goNext = () => { if (scrollerRef.current) scrollerRef.current.scrollLeft += 200; };

    const todayY = new Date().getFullYear();
    const todayM = new Date().getMonth() + 1;
    const isFuture = (y, m) => (y > todayY) || (y === todayY && m > todayM);
    const isCurrentMonth = (y, m) => (y === todayY && m === todayM);

    return (
        <div className="flex items-center gap-1 bg-slate-50 rounded-md border border-slate-200 p-0.5">
            <button onClick={goPrev} className="h-12 w-4 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm" title="Précédent">‹</button>
            <div ref={scrollerRef} className="flex-1 overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
                <div className="min-w-max" data-testid="month-shortcuts">
                    {/* Months row */}
                    <div className="flex" style={{ gap: `${WEEK_GAP}px` }}>
                        {monthGroups.map((g) => {
                            const isActive = selectedMonthKey === g.key;
                            const isCur = isCurrentMonth(g.year, g.month);
                            const isFut = isFuture(g.year, g.month);
                            const width = g.nWeeks * WEEK_W + (g.nWeeks - 1) * WEEK_GAP;
                            return (
                                <button
                                    key={g.key}
                                    type="button"
                                    onClick={() => handleMonthClick(g.year, g.month)}
                                    title={`${MONTHS_FR[g.month - 1]} ${g.year}`}
                                    disabled={isFut}
                                    className={`shrink-0 h-5 mb-0.5 rounded border text-[10px] font-semibold transition-colors leading-none ${
                                        isActive
                                            ? 'bg-slate-900 text-white border-slate-900'
                                            : isCur
                                                ? 'bg-blue-50 text-blue-700 border-blue-300'
                                                : isFut
                                                    ? 'bg-white text-slate-300 border-slate-200'
                                                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                    }`}
                                    style={{ width: `${width}px` }}
                                    data-testid={`month-${g.year}-${g.month}`}
                                >
                                    {MONTHS_FR[g.month - 1]}
                                    {g.month === 1 && <span className="ml-1 opacity-50">{g.year}</span>}
                                </button>
                            );
                        })}
                    </div>
                    {/* Weeks row */}
                    <div className="flex" style={{ gap: `${WEEK_GAP}px` }}>
                        {weeks.map((mon, i) => {
                            const monIso = toIsoDate(mon);
                            const sun = addDays(mon, 6);
                            const daysSet = coverageMap?.[monIso];
                            const count = daysSet ? daysSet.size : 0;
                            const isCur = sameWeek(mon, new Date());
                            const status = count === 0 ? 'missing' : count >= 7 ? 'full' : 'partial';
                            const selected = isSelected(monIso);
                            const dotClass = status === 'full'
                                ? 'bg-emerald-500'
                                : status === 'partial' ? 'bg-amber-400' : 'bg-slate-300';
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={(e) => handleClick(mon, e)}
                                    title={`S${isoWeekNumber(mon)} — ${monIso} → ${toIsoDate(sun)}${count > 0 ? ` (${count}/7 j)` : ''}\nShift+clic = plage`}
                                    className={`shrink-0 py-0.5 rounded border text-[9px] leading-tight text-center transition-colors ${
                                        selected
                                            ? 'bg-slate-900 text-white border-slate-900'
                                            : isCur
                                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                    }`}
                                    style={{ width: `${WEEK_W}px` }}
                                    data-testid={`pivot-week-${monIso}`}
                                >
                                    <div className="font-bold">S{isoWeekNumber(mon)}</div>
                                    <div className={`text-[8px] ${selected ? 'text-slate-300' : 'text-slate-400'}`}>
                                        {String(mon.getDate()).padStart(2, '0')}/{String(mon.getMonth() + 1).padStart(2, '0')}
                                    </div>
                                    <div className="flex justify-center mt-0.5">
                                        <span className={`w-1 h-1 rounded-full ${dotClass}`} />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <button onClick={goNext} className="h-12 w-4 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm" title="Suivant">›</button>
        </div>
    );
}

// ----------- Pivot Table render (unchanged) -----------

function PivotTable({ pivot, primaryMetric, cellBg, fmtMetric }) {
    const { rows, cols, cells, totals_by_row, totals_by_col, grand_total, metrics, row_dimensions, col_dimensions } = pivot;
    const rowDims = row_dimensions || [];
    const colDims = col_dimensions || [];
    const showMultiMetrics = metrics.length > 1;
    const cellMinW = showMultiMetrics ? metrics.length * 60 : 70;

    return (
        <table className="text-xs border-collapse" data-testid="pivot-table">
            <thead className="sticky top-0 z-20">
                {colDims.length > 0 && colDims.map((cd, ci) => (
                    <tr key={`coldim-${ci}`} className="bg-slate-50 border-b">
                        {ci === 0 && rowDims.map((rd, ri) => (
                            <th
                                key={`rd-${ri}`}
                                rowSpan={colDims.length}
                                className="text-left p-2 sticky left-0 bg-slate-100 border-r font-semibold text-slate-700 z-10"
                                style={{ left: ri * 140 }}
                            >
                                {DIMENSIONS.find(d => d.id === rd)?.label || rd}
                            </th>
                        ))}
                        {ci === 0 && rowDims.length === 0 && (
                            <th rowSpan={colDims.length} className="sticky left-0 bg-slate-100 border-r p-2 z-10"></th>
                        )}
                        {cols.map((c) => (
                            <th
                                key={c.key + '-' + ci}
                                colSpan={showMultiMetrics ? metrics.length : 1}
                                className="text-center p-1.5 font-semibold text-slate-600 border-r whitespace-nowrap"
                                style={{ minWidth: cellMinW }}
                                title={c.labels.join(' / ')}
                            >
                                {c.labels[ci]}
                            </th>
                        ))}
                        {ci === 0 && (
                            <th
                                rowSpan={colDims.length + (showMultiMetrics ? 1 : 0)}
                                colSpan={showMultiMetrics ? metrics.length : 1}
                                className="text-center p-1.5 font-bold text-slate-700 bg-slate-200 sticky right-0 z-10"
                                style={{ minWidth: cellMinW }}
                            >
                                Total
                            </th>
                        )}
                    </tr>
                ))}
                {showMultiMetrics && (
                    <tr className="bg-slate-100 border-b text-[10px] text-slate-500 uppercase">
                        {rowDims.length === 0 && colDims.length === 0 && (
                            <th className="sticky left-0 bg-slate-100 border-r p-1"></th>
                        )}
                        {colDims.length === 0 && rowDims.map((rd, ri) => (
                            <th
                                key={`rdm-${ri}`}
                                className="text-left p-2 sticky left-0 bg-slate-100 border-r font-semibold text-slate-700 z-10"
                                style={{ left: ri * 140 }}
                            >
                                {DIMENSIONS.find(d => d.id === rd)?.label || rd}
                            </th>
                        ))}
                        {cols.map(c => metrics.map(m => (
                            <th key={c.key + '-' + m} className="text-center px-1 py-0.5 border-r font-semibold">
                                {METRICS.find(x => x.id === m)?.label || m}
                            </th>
                        )))}
                        {colDims.length === 0 && metrics.map(m => (
                            <th key={'tot-' + m} className="text-center px-1 py-0.5 border-r font-semibold bg-slate-200">
                                {METRICS.find(x => x.id === m)?.label || m}
                            </th>
                        ))}
                    </tr>
                )}
                {colDims.length === 0 && !showMultiMetrics && (
                    <tr className="bg-slate-50 border-b">
                        {rowDims.map((rd, ri) => (
                            <th
                                key={`rdc-${ri}`}
                                className="text-left p-2 sticky bg-slate-100 border-r font-semibold text-slate-700 z-10"
                                style={{ left: ri * 140 }}
                            >
                                {DIMENSIONS.find(d => d.id === rd)?.label || rd}
                            </th>
                        ))}
                        <th className="text-center p-1.5 font-bold text-slate-700 bg-slate-200">
                            {METRICS.find(x => x.id === primaryMetric)?.label}
                        </th>
                    </tr>
                )}
            </thead>
            <tbody>
                {rows.map(r => {
                    const rowTotal = totals_by_row[r.key] || {};
                    return (
                        <tr key={r.key} className="border-b hover:bg-slate-50/60" data-testid={`pivot-row-${r.key}`}>
                            {r.labels.map((lbl, ri) => (
                                <td
                                    key={ri}
                                    className="p-1.5 sticky left-0 bg-white border-r font-medium text-slate-800 z-10"
                                    style={{ left: ri * 140, minWidth: 140, maxWidth: 240 }}
                                    title={lbl}
                                >
                                    <div className="truncate">{lbl}</div>
                                </td>
                            ))}
                            {cols.length === 0 ? (
                                metrics.map(m => (
                                    <td key={m} className={`p-1.5 text-right tabular-nums font-semibold bg-slate-50`}>
                                        {fmtMetric(m, rowTotal[m])}
                                    </td>
                                ))
                            ) : cols.map(c => {
                                const cell = cells[`${r.key}__${c.key}`];
                                if (showMultiMetrics) {
                                    return metrics.map(m => (
                                        <td key={c.key + '-' + m} className={`p-1 text-right tabular-nums border-r ${m === primaryMetric ? cellBg(cell?.[m] || 0) : ''}`}>
                                            {cell ? fmtMetric(m, cell[m]) : <span className="text-slate-200">·</span>}
                                        </td>
                                    ));
                                }
                                const v = cell ? cell[primaryMetric] : 0;
                                return (
                                    <td key={c.key} className={`p-1 text-right tabular-nums border-r ${cellBg(v)}`}>
                                        {cell ? fmtMetric(primaryMetric, v) : <span className="text-slate-200">·</span>}
                                    </td>
                                );
                            })}
                            {cols.length > 0 && (
                                showMultiMetrics ? metrics.map(m => (
                                    <td key={'tot-' + m} className="p-1.5 text-right tabular-nums font-bold bg-slate-100 sticky right-0">
                                        {fmtMetric(m, rowTotal[m])}
                                    </td>
                                )) : (
                                    <td className="p-1.5 text-right tabular-nums font-bold bg-slate-100 sticky right-0">
                                        {fmtMetric(primaryMetric, rowTotal[primaryMetric])}
                                    </td>
                                )
                            )}
                        </tr>
                    );
                })}
                <tr className="bg-slate-100 border-t-2 border-slate-300 font-bold">
                    <td
                        className="p-2 sticky left-0 bg-slate-100 border-r"
                        colSpan={Math.max(rowDims.length, 1)}
                    >
                        Total
                    </td>
                    {cols.length === 0 ? (
                        metrics.map(m => (
                            <td key={m} className="p-1.5 text-right tabular-nums bg-slate-200">
                                {fmtMetric(m, grand_total[m])}
                            </td>
                        ))
                    ) : cols.map(c => {
                        const t = totals_by_col[c.key] || {};
                        if (showMultiMetrics) {
                            return metrics.map(m => (
                                <td key={c.key + '-tot-' + m} className="p-1 text-right tabular-nums border-r bg-slate-100">
                                    {fmtMetric(m, t[m])}
                                </td>
                            ));
                        }
                        return (
                            <td key={c.key} className="p-1 text-right tabular-nums border-r bg-slate-100">
                                {fmtMetric(primaryMetric, t[primaryMetric])}
                            </td>
                        );
                    })}
                    {cols.length > 0 && (
                        showMultiMetrics ? metrics.map(m => (
                            <td key={'g-' + m} className="p-1.5 text-right tabular-nums bg-slate-200 sticky right-0">
                                {fmtMetric(m, grand_total[m])}
                            </td>
                        )) : (
                            <td className="p-1.5 text-right tabular-nums bg-slate-200 sticky right-0">
                                {fmtMetric(primaryMetric, grand_total[primaryMetric])}
                            </td>
                        )
                    )}
                </tr>
            </tbody>
        </table>
    );
}

// Helpers
function toIsoDate(d) { return d.toISOString().split('T')[0]; }
function mondayOf(dateIso) {
    const d = new Date(dateIso + 'T00:00:00');
    const dow = d.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + diff);
    return toIsoDate(d);
}
function sameWeek(d1, d2) { return mondayOf(toIsoDate(d1)) === mondayOf(toIsoDate(d2)); }
function isoWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function todayStr() { return new Date().toISOString().split('T')[0]; }
function daysAgoStr(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; }
